import React, { useState, useEffect, useRef } from 'react';

const capyImages = [
  'https://becapyworld.com/images/main_capy1.png',
  'https://becapyworld.com/images/main_capy2.png',
  'https://becapyworld.com/images/main_capy3.png',
  'https://becapyworld.com/images/main_capy4.png',
  'https://becapyworld.com/images/main_capy5.png'
];

// Removed declare global and type declarations for gtag and dataLayer


export default function CapyChat() {
  // Safe localStorage wrappers to avoid errors in environments without localStorage
  const safeGet = (key, defaultValue = null) => {
    try {
      // Check if localStorage is available
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem(key);
      }
      return defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const safeSet = (key, value) => {
    try {
      // Check if localStorage is available
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(key, value);
      }
    } catch {} // Ignore errors if localStorage is not available
  };

  // URL param parser for initial configuration
  const getQueryParam = key => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get(key);
  };

  // State variables to manage the application's data and UI
  const [audioSrc, setAudioSrc] = useState(null); // URL for the currently playing audio
  const [messages, setMessages] = useState([]); // Array of chat messages
  const [input, setInput] = useState(''); // Current input text in the message box
  const [capyName, setCapyName] = useState(''); // The name of the Capybara
  const [capyBirthday, setCapyBirthday] = useState(''); // The Capybara's birthday
  const [capyVoice, setCapyVoice] = useState(null); // The selected voice for text-to-speech
  const [knowledge, setKnowledge] = useState(''); // Knowledge base content
  // New state to track if knowledge base failed to load
  const [knowledgeLoadError, setKnowledgeLoadError] = useState(false);
  // Memories stored in localStorage, now a nested structure keyed by Capy name
  const [allCapyMemories, setAllCapyMemories] = useState(() => {
    const storedMemories = safeGet('allCapyMemories'); // Use a new key for the nested structure
    return storedMemories ? JSON.parse(storedMemories) : {};
  });

  // Effect to persist ALL Capy memories to localStorage whenever they change
  useEffect(() => {
    safeSet('allCapyMemories', JSON.stringify(allCapyMemories));
  }, [allCapyMemories]); // Dependency array includes allCapyMemories

  // State for the user's name, derived from the current Capy's memories
  const userName = allCapyMemories[capyName]?.userName || '';

  // New state to indicate if the app is currently waiting for the user's new name
  const [isChangingName, setIsChangingName] = useState(false);
  const [loading, setLoading] = useState(true); // Loading state indicator
  const [usingChatGPT, setUsingChatGPT] = useState(false); // Flag to indicate if ChatGPT was used for the last reply
  const [isSending, setIsSending] = useState(false); // Flag to prevent multiple messages being sent at once
  // Sound state, persisted in localStorage, now defaults to true if not set
  const [soundOn, setSoundOn] = useState(() => {
    const storedSound = safeGet('soundOn');
    // If storedSound is null (not in localStorage), default to true. Otherwise, use the stored boolean value.
    return storedSound === null ? true : storedSound === 'true';
  });
  const [headerImg, setHeaderImg] = useState(null); // Image URL for the header

  // Effect to set a random header image on component mount
  useEffect(() => {
    const randomIdx = Math.floor(Math.random() * capyImages.length);
    setHeaderImg(capyImages[randomIdx]);
  }, []); // Empty dependency array means this runs only once on mount

  // State for auto-ping feature
  const [pingCount, setPingCount] = useState(0); // Counter for pings within an hour
  const [lastPingReset, setLastPingReset] = useState(Date.now()); // Timestamp of the last ping count reset

  // Refs for timers and auto-scrolling
  const pingTimerRef = useRef(null); // Ref for the auto-ping timer
  const resetTimerRef = useRef(null); // Ref for the ping count reset timer
  const chatEndRef = useRef(null); // Ref for the end of the chat window for scrolling

  // Helper function to strip emojis from text (as speech synthesis can struggle with them)
  const stripEmojis = text => text.replace(/([\uD800-\uDBFF][\uDC00-\uDFFF])/g, '');

  // Text-to-speech function
  const speakText = (text, voice) => {
    // Only speak if text, voice, and sound are enabled
    if (!text || !voice || !soundOn) return;
    // Cancel any ongoing speech
    speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(stripEmojis(text));
    utter.voice = voice;
    utter.rate = 1.3; // Slightly faster than default
    utter.pitch = 2; // Higher pitch
    speechSynthesis.speak(utter);
  };

  // Fallback reply function if ChatGPT fails or is not used
  const getFallbackReply = inputText => {
    const lower = inputText.toLowerCase();
    // Check for common greetings
    if (/\b(hi|hello|hey|sup|greetings)\b/.test(lower)) {
      const greets = ['Hey there!', 'Hello, friend!', 'Capy here!', 'Hiya!'];
      return greets[Math.floor(Math.random() * greets.length)];
    }
    // Default fallback
    return 'ʕ•ᴥ•ʔ';
  };

  // Basic appropriateness check for names
  const isNameAppropriate = (name) => {
      const lowerName = name.toLowerCase();
      // Add a list of inappropriate words to check against
      const inappropriateWords = ['badword1', 'badword2', 'offensive', 'swear', 'rude']; // Replace with actual list
      for (const word of inappropriateWords) {
          if (lowerName.includes(word)) {
              return false;
          }
      }
      // Add other checks like length, presence of numbers/symbols if needed
      if (name.length < 2 || name.length > 20) return false; // Example length check
      // Basic check for only letters, spaces, and hyphens
      if (!/^[a-zA-Z\s-]+$/.test(name)) return false;
      return true;
  };


  // Function to call the ChatGPT proxy API
  const callChatGPT = async (userInput, isNameExtraction = false, isMotdGeneration = false) => {
    try {
      // Get origin and home from the current Capy's memories
      const currentCapyMemories = allCapyMemories[capyName] || {};
      const { origin, home } = currentCapyMemories;

      // Construct the personality block for the system prompt, including user name if available
      const personalityBlock =
        `You are ${capyName}, a friendly capybara born on ${currentCapyMemories.birthdate || 'an unknown date'}, from ${home || 'an unknown place'}.` + // Use birthdate and home from current memories
        ` Your personality is kind, supportive, and playful with a hint of rizz.` +
        (userName ? ` The user's name is ${userName}.` : ''); // Add user name to prompt if known

      let systemPrompt;
      let apiMessages;

      if (isNameExtraction) {
          // More explicit prompt for name extraction
          systemPrompt = `Analyze the following message from the user: "${userInput}". If the user is clearly stating or indicating their name, respond with ONLY that name. If the message does not contain a clear indication of the user's name, respond with ONLY the word 'Unknown'. Do not include any other text, punctuation, or conversational filler.`;
          apiMessages = [{ role: 'system', content: systemPrompt }];
      } else if (isMotdGeneration) {
          // Specific prompt for MOTD generation
          const currentDate = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
          systemPrompt = `Generate a short, fun message of the day from the perspective of a friendly capybara. Include a reference to today's date (${currentDate}) and any relevant common occasion, holiday, or season. Keep it concise and cheerful. Respond with only the message text.`;
          apiMessages = [{ role: 'system', content: systemPrompt }];
      }
      else {
          // Standard chat prompt
          systemPrompt =
            `${personalityBlock}\n\nKnowledge Base:\n${knowledge}\n\nMemories:\n${JSON.stringify(currentCapyMemories)}`; // Pass only the current Capy's memories
          apiMessages = [
            { role: 'system', content: systemPrompt },
            // Map existing messages to the API format, ensuring content is not null/undefined
            ...messages.map(m => ({
              role: m.role === 'user' ? 'user' : 'assistant',
              content: m.text || '' // Use empty string if text is null/undefined
            })),
            { role: 'user', content: userInput } // Add the current user input
          ];
      }


      // Make the POST request to the proxy API
      const response = await fetch('https://capy-chat-proxy.mark-6af.workers.dev/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo', // Specify the model
          messages: apiMessages // Send the prepared messages
        })
      });

      // Parse the JSON response
      const data = await response.json();
      // Return the content of the first choice's message, or null if not available
      return data.choices?.[0]?.message?.content || null;
    } catch (err) {
      // Log any errors and return null
      console.error("Error calling ChatGPT API:", err); // Log the error
      return null;
    }
  };

  // Function to handle sending a message
  const sendMessage = async () => {
    // Prevent sending if input is empty or already sending
    if (!input.trim() || isSending) return;

    const userInput = input.trim(); // Store the current input and trim whitespace
    setInput(''); // Clear the input field
    setIsSending(true); // Set sending state to true

    // Check if this is the very first message from the user
    const isFirstUserMessage = messages.length === 1 && messages[0].role === 'capy';

    // --- Handle Name Change Command ---
    if (isChangingName) {
        // If we are currently waiting for a new name
        const newName = userInput;
        if (isNameAppropriate(newName)) {
            // setUserName(newName); // This state is derived, so update memories instead
            // Update memories with the new user name for the current Capy
            setAllCapyMemories(prevAllMemories => ({
                ...prevAllMemories,
                [capyName]: { // Update the memories for the current Capy
                    ...(prevAllMemories[capyName] || {}),
                    userName: newName
                }
            }));

            // Array of fun, in-character confirmation messages
            const confirmationPhrases = [
                `Whatever you want to be called is fine by me, ${newName}!`,
                `Got it, ${newName}! That's a lovely name!`,
                `Alrighty, ${newName}! I'll remember that!`,
                `Consider it done, ${newName}! Nice to officially meet you (again)!`,
                `Okay, ${newName}! Your wish is my capy command!`,
                `Boop! Name updated to ${newName}!`,
                `Fantastic, ${newName}! Let's chat!`,
                `From now on, you are ${newName} to me!`,
                `Roger that, ${newName}! Ready when you are!`,
                `Switching gears to ${newName}! How can I help?`
            ];
            const confirmation = confirmationPhrases[Math.floor(Math.random() * confirmationPhrases.length)];
            console.log("Generated confirmation:", confirmation); // Log the generated confirmation
            setMessages(prev => [...prev, { role: 'user', text: userInput }, { role: 'capy', text: confirmation }]);
            speakText(confirmation, capyVoice);
            setIsChangingName(false); // Reset name change state
        } else {
            const inappropriateMsg = "Hmm, that name doesn't seem quite right. Could you try a different one? Please keep it friendly and appropriate for all ages!";
            setMessages(prev => [...prev, { role: 'user', text: userInput }, { role: 'capy', text: inappropriateMsg }]);
            speakText(inappropriateMsg, capyVoice);
            // Stay in isChangingName state to wait for another attempt
        }
        setIsSending(false); // Reset sending state
        return; // Stop processing
    }

    // Check for name change command
    if (userInput.toLowerCase() === 'change my name') {
        setIsChangingName(true); // Set state to indicate we are changing name
        const prompt = "Okay, what would you like me to call you instead?";
        setMessages(prev => [...prev, { role: 'user', text: userInput }, { role: 'capy', text: prompt }]);
        speakText(prompt, capyVoice);
        setIsSending(false); // Reset sending state
        return; // Stop processing
    }


    // Add the user's message to the chat immediately
    setMessages(prev => [...prev, { role: 'user', text: userInput }]);

    let extractedName = '';

    // --- Attempt to Capture or Update User Name ---
    // Always attempt to extract name using GPT for any user message
    const gptExtractedName = await callChatGPT(userInput, true); // Pass true to indicate name extraction

    if (gptExtractedName && gptExtractedName.toLowerCase().trim() !== 'unknown') {
        extractedName = gptExtractedName.trim();
        // Capitalize the first letter
        if (extractedName) {
             extractedName = extractedName.charAt(0).toUpperCase() + extractedName.slice(1);
        }
    } else {
        // Fallback: if GPT fails or returns 'Unknown', try a more cautious manual extraction
        const lowerInput = userInput.toLowerCase().trim();
        // Only attempt fallback extraction if a clear introductory phrase is present
        if (lowerInput.startsWith("my name is ")) {
            extractedName = lowerInput.substring("my name is ".length).trim();
        } else if (lowerInput.startsWith("i'm ")) {
            extractedName = lowerInput.substring("i'm ".length).trim();
        } else if (lowerInput.startsWith("i am ")) {
             extractedName = lowerInput.substring("i am ".length).trim();
        }

         // Capitalize the first letter of the fallback name
        if (extractedName) {
             extractedName = extractedName.charAt(0).toUpperCase() + extractedName.slice(1);
        }
    }

    // If a valid name was extracted (either by GPT or fallback) AND it's different from the current name
    // Also explicitly allow "Friend" as a valid name
    if (extractedName && extractedName.toLowerCase() !== userName.toLowerCase() && (extractedName.toLowerCase() === 'friend' || isNameAppropriate(extractedName))) {
        // setUserName(extractedName); // This state is derived, update memories instead
         // Update memories with the new user name for the current Capy
        setAllCapyMemories(prevAllMemories => ({
            ...prevAllMemories,
            [capyName]: { // Update the memories for the current Capy
                ...(prevAllMemories[capyName] || {}),
                userName: extractedName
            }
        }));

        // Give a simple acknowledgement using the new extracted name
        const acknowledgement = `Okay, I'll call you ${extractedName} from now on! Nice to meet you (again)!`;
        setMessages(prev => [...prev, { role: 'capy', text: acknowledgement }]);
        speakText(acknowledgement, capyVoice);
        setIsSending(false); // Reset sending state
        return; // Stop processing after updating and acknowledging name
    }

    // --- Speak Initial Message if it's the first user message ---
    if (isFirstUserMessage && soundOn && messages.length > 0) {
         // Find the initial Capy message (should be the first one)
         const initialCapyMessage = messages.find(msg => msg.role === 'capy');
         if (initialCapyMessage) {
             speakText(initialCapyMessage.text, capyVoice);
         }
    }


    // --- Handle Regular Messages (after name is set or not updated) ---
    handleRegularMessage(userInput);

  };

  // New function to handle regular message processing (special responses and ChatGPT call)
  const handleRegularMessage = async (userInput) => {
      // --- Special Responses ---

      // Amelie special response
      if (/\bamelie\b/i.test(userInput)) {
        const ameliePhrases = [
          'I knew a girl named Amelie.',
          'Where did you go Amelie, Amelie, Amelie?',
          'I met a girl once. She nearly ripped me open. She doesn\'t even know it.',
          'I knew a girl named Amelie, or were you all in a dream?',
          'Or were you all in a dream, Amelie, Amelie, Amelie?'
        ];
        const phrase = ameliePhrases[Math.floor(Math.random() * ameliePhrases.length)];
        // Add Capy's special reply to messages
        setMessages(prev => [...prev, { role: 'capy', text: phrase }]);
        // Speak the phrase
        speakText(phrase, capyVoice);
        setIsSending(false);
        return; // Stop processing after special response
      }

      // Marla special response
      if (/\bjaffe\b/i.test(userInput)) {
        const marlaPhrases = [
          'Best. Teacher. Ever.',
          'Is that the comedian who daylights as a teacher?',
          'This wouldn\t be possible without her support, you know.',
          'You mean Marla E Jaff, President of Mars?',
        ];
        const phrase = marlaPhrases[Math.floor(Math.random() * marlaPhrases.length)];
        setMessages(prev => [...prev, { role: 'capy', text: phrase }]);
        speakText(phrase, capyVoice);
        setIsSending(false);
        return;
      }

      // Gracie special response (using Abrams as trigger based on common association)
      if (/\babrams\b/i.test(userInput)) {
        const abramsPhrases = [
          'Or were you all in a dream Amelie, Amelie?',
          'Ooh, bet you\'re thinking she\'s so cool. Kicking back on your couch, making eyes from across the room. Wait, I think I\'ve been there too',
          'I met a girl once. She nearly ripped me open. She doesn\'t even know it.',
          'Watch this be the wrong thing. Classic',
          'Think I really want this. I\'m not even kidding. No, I\'m actually invested. Haven\'t even met yet'
        ];
        const phrase = abramsPhrases[Math.floor(Math.random() * abramsPhrases.length)];
        setMessages(prev => [...prev, { role: 'capy', text: phrase }]);
        speakText(phrase, capyVoice);
        setIsSending(false);
        return;
      }

      // Music trigger
      const lower = userInput.toLowerCase();
      if (lower.includes('music') || lower.includes('song')) {
        // Choose a random track
        const trackUrl = Math.random() < 0.5
          ? 'https://becapyworld.com/knowledge/1.mp3'
          : 'https://becapyworld.com/knowledge/2.mp3';
        setAudioSrc(trackUrl); // Set the audio source to play
        const phrases = [
          "I love music! Here's a song I love!",
          "This tune always makes me dance!",
          "Time for a groovy capy beat!",
          "Here's a melody to brighten your day!",
          "Let's jam with this awesome track!"
        ];
        const phrase = phrases[Math.floor(Math.random() * phrases.length)];
        setMessages(prev => [...prev, { role: 'capy', text: phrase }]);
        setIsSending(false);
        return; // Stop processing after music trigger
      }

      // --- End Special Responses ---

      // Call ChatGPT for a response
      const reply = await callChatGPT(userInput);
      // Use the ChatGPT reply if available, otherwise use the fallback
      const output = reply || getFallbackReply(userInput);

      // Add Capy's reply to the chat
      setUsingChatGPT(!!reply); // Set flag based on whether ChatGPT provided a reply
      setMessages(prev => [...prev, { role: 'capy', text: output }]);
      // Speak Capy's reply
      speakText(output, capyVoice);
      setIsSending(false); // Reset sending state
  }


  // Auto-ping scheduler function
  const schedulePing = () => {
    // Clear any existing ping timer
    if (pingTimerRef.current) clearTimeout(pingTimerRef.current);

    // Calculate a random delay between 30 and 45 seconds
    const delay = 30000 + Math.random() * 15000;

    // Set the ping timer
    pingTimerRef.current = setTimeout(() => {
      const now = Date.now();
      // Check if an hour has passed since the last reset
      if (now - lastPingReset >= 3600000) {
        setPingCount(0); // Reset ping count
        setLastPingReset(now); // Update last reset time
      }

      // If less than 2 pings in the current hour and not currently changing name
      if (pingCount < 2 && !isChangingName) {
        let ping;
        // Check if user name is set
        if (!userName) {
             // If user name is not set, ask for it in a fun way
             const askNamePhrases = [
                 "Hey, I just realized... I don't know your name yet! What is it?",
                 "Quick question! What should I call you?",
                 "Pardon my capy memory, but could you remind me of your name?",
                 "My brain is a little muddy today... what's your name again?",
                 "I'd love to know your name if you're comfortable sharing!"
             ];
             ping = askNamePhrases[Math.floor(Math.random() * askNamePhrases.length)];
        } else {
            // If user name is set, use a generic ping
            const genericPings = [
              `Hey ${userName}, just wanted to say hi!`,
              `Thinking of you, ${userName}! Hope you're having a great day!`,
              `Capy-bara-zing by to see if you're there, ${userName}!`,
              `Just chilling here, ${userName}. What are you up to?`,
              `Sending some relaxed vibes your way, ${userName}!`,
              `Did you know capybaras are great swimmers, ${userName}? Just a random thought!`,
              `Anything exciting happening, ${userName}?`,
              `Hope you're feeling as chill as a capybara in water, ${userName}!`,
              `Just a friendly wave from ${capyName}, ${userName}!`,
              `Checking in, ${userName}! All good?`
            ];
            ping = genericPings[Math.floor(Math.random() * genericPings.length)];
        }

        // Add ping message and speak it
        setMessages(prev => [...prev, { role: 'capy', text: ping }]);
        speakText(ping, capyVoice);
        setPingCount(prev => prev + 1); // Increment ping count
        schedulePing(); // Schedule the next ping
      } else {
        // If 2 pings reached or currently changing name, schedule a reset after the hour is up
        const timeLeft = lastPingReset + 3600000 - now;
        resetTimerRef.current = setTimeout(() => {
          setPingCount(0); // Reset count
          setLastPingReset(Date.now()); // Set new reset time
          schedulePing(); // Start pinging again
        }, timeLeft);
      }
    }, delay);
  };

  // Effect to start the ping scheduler when messages are added (after the first user message)
  useEffect(() => {
      // Start pinging only after the initial message(s) are loaded and the first user message is sent.
      // We can check if there's more than one message (the initial Capy message + the first user message)
      if (messages.length > 1 && messages[1].role === 'user') {
          schedulePing();
      }

      // Cleanup function to clear timers
      return () => {
        if (pingTimerRef.current) clearTimeout(pingTimerRef.current);
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      };
  }, [messages.length, userName, isChangingName]); // Dependencies to re-run when message count, user name, or name change state changes


  // Effect for auto-scrolling to the latest message
  useEffect(() => {
    // Scroll the element referenced by chatEndRef into view with smooth behavior
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]); // Dependency array includes messages, so it scrolls when new messages are added


  // Initialization effect: runs once on component mount
  useEffect(() => {
    // --- Capy Name Selection ---
    // Get stored Capy name from memories or old key for backward compatibility
    const storedAllCapyMemories = safeGet('allCapyMemories');
    const parsedAllCapyMemories = storedAllCapyMemories ? JSON.parse(storedAllCapyMemories) : {};

    const storedCapyName = parsedAllCapyMemories.lastCapyName || safeGet('capyName'); // Remember last used Capy name
    let capyNameValue;
    const param = getQueryParam('name'); // Check for name in URL parameters
    const nameList = ['Mello','Snuffy','Rizzles','Bubbles','Tapioca','Peachy','Waffles','Nugget','Fluffernut','Toffee','Snort','Ripley','Weyland','Churro','Thomas','Sizzlestar','Sparkles'];

    let nameIndex; // Variable to store the index used for name and favorites

    if (param) {
      // If name param exists, use the index derived from the param
      nameIndex = parseInt(param, 10) % nameList.length;
      capyNameValue = nameList[nameIndex];
    } else if (storedCapyName) {
      // If no param but name is stored, use the stored name
      capyNameValue = storedCapyName;
      nameIndex = nameList.indexOf(storedCapyName);
      // If stored name is not in the current list (e.g., list changed), default to a random index
      if (nameIndex === -1) {
         nameIndex = Math.floor(Math.random() * nameList.length);
         capyNameValue = nameList[nameIndex];
      }
    } else {
      // If no param and no stored name, choose a random index and name
      nameIndex = Math.floor(Math.random() * nameList.length);
      capyNameValue = nameList[nameIndex];
    }
    setCapyName(capyNameValue); // Set the Capy's name state

    // Ensure memories for the current Capy name exist
    const currentCapyMemories = parsedAllCapyMemories[capyNameValue] || {};

    // --- Birthday ---
    // Get birthday from current Capy's memories or set to today's date
    const birth = currentCapyMemories.birthdate || new Date().toLocaleString('default', { month: 'long', day: 'numeric' });

    // --- Favorites Arrays ---
    const colors = ['blue','turquoise','yellow','lavender','mint green','coral pink','sky blue','lime green','peach','aqua'];
    const snacks = ['carrot sticks','strawberries','apple slices','grapes','cucumber slices','cherry tomatoes','pears','blueberries','snap peas','banana','mango slices'];
    const hobbies = ['dancing in the rain','drawing silly cartoons','building pillow forts','telling funny jokes','making up silly songs','climbing trees and rocks','chasing bubbles','dancing with rizz','making dandelion wishes','pretending to fly'];

    // Choose favorites using the same nameIndex for consistency, or load from current Capy's stored memories
    const favColor = currentCapyMemories.color || colors[nameIndex % colors.length];
    const favSnack = currentCapyMemories.snack || snacks[nameIndex % snacks.length];
    const favHobby = currentCapyMemories.hobby || hobbies[nameIndex % hobbies.length];

    // --- Origin & Home (for memory only) ---
    const origin = 'Glimmer Grove, hidden away in South America';
    const homes = ['Riverheart Village','Sunmarsh Meadows','Misty Glade','Cascade Falls'];
    // Use nameIndex for home consistency as well, or load from current Capy's stored memories
    const home = currentCapyMemories.home || homes[nameIndex % homes.length];

    // --- User Name ---
    const storedUserName = currentCapyMemories.userName || ''; // Get stored user name for THIS Capy
    // userName is now derived state, no need to set it directly here

    // --- Initial Messages (Welcome Intro / Welcome Back + Optional MOTD) ---
    let initialMessages = [];
    // Check if memories for THIS Capy exist and user name is stored
    const isReturningUserForThisCapy = currentCapyMemories && Object.keys(currentCapyMemories).length > 0 && storedUserName;

    if (isReturningUserForThisCapy) {
        // Returning user for THIS Capy: Choose a random welcome back message and include the stored name
        const welcomeBackPhrases = [
            `Hey there! Welcome back, ${storedUserName}!`,
            `Guess who's back? It's you, ${storedUserName}! Hi again!`,
            `Well hello there, ${storedUserName}! Good to see you again!`,
            `Welcome back to our chat, ${storedUserName}! Ready for some fun?`,
            `Hiya, ${storedUserName}! So glad you're here again!`
        ];
        const welcomeBackMessage = welcomeBackPhrases[Math.floor(Math.random() * welcomeBackPhrases.length)];
        initialMessages.push({ role: 'capy', text: welcomeBackMessage });

        // --- Load Message of the Day (MOTD) for returning users ---
        // MOTD is shared across all Capys, so check the shared MOTD data
        const storedMotdData = safeGet('capyMotdData');
        const today = new Date().toDateString(); // Get today's date string

        if (storedMotdData) {
            const { date, motdText } = JSON.parse(storedMotdData);
            if (date === today && motdText) {
                // If stored MOTD is from today, use it
                initialMessages.push({ role: 'motd', text: motdText });
                 // Optionally speak the MOTD if sound is on
                // Speak after a small delay if the welcome message is also spoken
                if (soundOn) setTimeout(() => speakText(motdText, capyVoice), 1000);
            } else {
                // If stored MOTD is old or doesn't exist, fetch/generate a new one
                loadAndSetMotd(initialMessages, soundOn, capyVoice);
            }
        } else {
             // If no MOTD data stored, fetch/generate a new one
             loadAndSetMotd(initialMessages, soundOn, capyVoice);
        }


    } else {
        // First-time user for THIS Capy or no stored user name for THIS Capy: Generate a random intro asking for name
        const intros = Array.from({ length: 10 }, () => {
          // Use the consistent favorites for the intro
          return `Wow, I'm ${capyNameValue}! My favorite color is ${favColor}, I love ${favSnack}, and I enjoy ${favHobby}. It's great to meet you! What's your name?`;
        });
        const initialIntro = intros[Math.floor(Math.random() * intros.length)];
        initialMessages.push({ role: 'capy', text: initialIntro });
        // Speaking of the initial intro will be handled on the first user message send
    }

    // Set the initial messages (welcome/intro and potentially MOTD)
    setMessages(initialMessages);


    // --- Save Initial Capy Memories ---
    // Save the initial memories for the current Capy, including loaded user name (if any)
     setAllCapyMemories(prevAllMemories => ({
        ...prevAllMemories,
        lastCapyName: capyNameValue, // Store the last used Capy name
        [capyNameValue]: {
            name: capyNameValue,
            birthdate: birth,
            color: favColor,
            snack: favSnack,
            hobby: favHobby,
            origin: origin,
            home: home,
            userName: storedUserName // Save the loaded user name for this Cap
        }
    }));


    // --- Load Knowledge Base ---
    // Fetch the knowledge base text file
    fetch(`/knowledge/knowledge_base.txt?ts=${Date.now()}`, { cache: 'no-store' })
      .then(res => {
        if (!res.ok) {
          // If response is not OK (e.g., 404), throw an error
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.text();
      })
      .then(setKnowledge) // Set the knowledge state
      .catch(() => {
        setKnowledge(''); // Set knowledge to empty string on error
        setKnowledgeLoadError(true); // Set knowledge load error state
      });


    // --- Voice Setup ---
    // Function to initialize the speech synthesis voice
    const initVoice = () => {
      const voices = speechSynthesis.getVoices(); // Get available voices
      const storedV = safeGet('capyVoiceName'); // Try to get stored voice name
      // Find the stored voice, or default to the first available voice
      const chosen = voices.find(v => v.name === storedV) || voices[0];
      setCapyVoice(chosen); // Set the Capy's voice state
      safeSet('capyVoiceName', chosen.name); // Save the chosen voice name
    };

    // If voices are not immediately available, wait for the 'voiceschanged' event
    if (typeof window !== 'undefined' && window.speechSynthesis && !window.speechSynthesis.getVoices().length) {
      speechSynthesis.addEventListener('voiceschanged', initVoice);
    } else if (typeof window !== 'undefined' && window.speechSynthesis) {
      // If voices are available, initialize immediately
      initVoice();
    }

    setLoading(false); // Set loading state to false once initialization is complete
  }, []); // Empty dependency array ensures this runs only once on mount

  // Helper function to load and set MOTD
  const loadAndSetMotd = async (currentMessages, soundOnStatus, currentCapyVoice) => {
       const today = new Date().toDateString(); // Get today's date string
       let finalMotd = '';

       try {
            // Fetch from the specified URL
            const res = await fetch('https://becapyworld.com/knowledge/motd.txt', { cache: 'no-store' });
            if (!res.ok) {
                // Log the specific HTTP error status
                console.error(`Failed to fetch MOTD file. Status: ${res.status}`);
                throw new Error(`MOTD file not found or error: ${res.status}`);
            }
            const motdText = await res.text();
            if (motdText.trim()) {
                finalMotd = motdText.trim();
            } else {
                // If file is empty, generate MOTD using GPT
                console.log("MOTD file is empty (via proxy), generating with GPT.");
                const generatedMotd = await callChatGPT('', false, true);
                if (generatedMotd) {
                    finalMotd = generatedMotd.trim();
                }
            }
       } catch (error) {
           console.error("Error loading MOTD file or generating with GPT:", error);
            // If fetching motd.txt failed, generate MOTD using GPT
            console.log("Attempting to generate MOTD with GPT due to fetch error.");
            const generatedMotd = await callChatGPT('', false, true);
            if (generatedMotd) {
                finalMotd = generatedMotd.trim();
            }
       }

       // Add MOTD as a message with role 'motd' and store in localStorage
       if (finalMotd) {
            // Use setMessages with a functional update to ensure we add to the current state
            setMessages(prev => [...prev, { role: 'motd', text: finalMotd }]);
            safeSet('capyMotdData', JSON.stringify({ date: today, motdText: finalMotd }));
             // Optionally speak the MOTD if sound is on
            if (soundOnStatus && currentMessages.length > 0) { // Only speak if there's a preceding message (like welcome)
                 setTimeout(() => speakText(finalMotd, currentCapyVoice), 1000); // Small delay after welcome
            } else if (soundOnStatus) {
                 // If no preceding message, speak immediately
                 speakText(finalMotd, currentCapyVoice);
            }
       } else {
           console.log("No MOTD loaded or generated."); // Log if no MOTD was set
       }
  };


  // Effect to start the ping scheduler when messages are added (after the first user message)
  useEffect(() => {
      // Start pinging only after the initial message(s) are loaded and the first user message is sent.
      // We can check if there's more than one message (the initial Capy message + the first user message)
      if (messages.length > 1 && messages[1].role === 'user') {
          schedulePing();
      }

      // Cleanup function to clear timers
      return () => {
        if (pingTimerRef.current) clearTimeout(pingTimerRef.current);
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      };
  }, [messages.length, userName, isChangingName]); // Dependencies to re-run when message count, user name, or name change state changes


  // Effect for auto-scrolling to the latest message
  useEffect(() => {
    // Scroll the element referenced by chatEndRef into view with smooth behavior
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]); // Dependency array includes messages, so it scrolls when new messages are added


  // Show a loading indicator while the component is initializing
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }

  // Tailwind CSS classes for message bubbles
  const userBubble = 'bg-blue-500 text-white ml-auto rounded-bl-3xl rounded-tl-3xl rounded-tr-xl';
  // Added new style for MOTD bubble
  const motdBubble = 'bg-yellow-200 text-gray-800 mx-auto rounded-xl p-2 text-center text-sm italic';
  const capyBubble = 'bg-white text-gray-800 mr-auto rounded-br-3xl rounded-tr-3xl rounded-tl-xl border border-gray-200';


  // Determine which bubble style to apply based on message role
  const getBubbleClass = (role) => {
      if (role === 'user') return userBubble;
      if (role === 'motd') return motdBubble;
      return capyBubble; // Default to Capy bubble for 'capy' role
  };


  // Render the chat UI
  return (
    // Main container with fixed dimensions and styling
    <div className={`flex flex-col w-[375px] h-[667px] bg-white rounded-3xl shadow-xl overflow-hidden mx-auto my-4`}>
      {/* Notch-like element for styling */}
      <div className="h-6 bg-gray-100 rounded-t-3xl flex items-center justify-center border-b border-gray-300">
        <div className="w-12 h-2 bg-gray-400 rounded-full" />
      </div>
      {/* Header section with Capy image and name */}
      <div className="flex flex-col items-center py-2">
        {/* Capy image */}
        <img src={headerImg} width="50" height="50" alt="Capy" />
        {/* Capy name */}
        <p className="text-lg font-bold mt-1">{capyName}</p>
      </div>

      {/* Chat Window - scrollable area for messages */}
      <div className="flex-1 bg-gray-100 p-4 overflow-y-auto space-y-2">
        {/* Map through messages and render each one */}
        {messages.map((msg, i) => (
          <div
            key={i} // Unique key for list items
            className={`max-w-[80%] p-3 break-words ${getBubbleClass(msg.role)}`} // Apply bubble styles based on role
          >
            {msg.text} {/* Display message text */}
          </div>
        ))}
        {/* Removed the separate MOTD rendering block */}
        <div ref={chatEndRef} /> {/* Auto-scroll ref */}
      </div>
      {/* Audio Player section */}
      {audioSrc && ( // Only render if audioSrc is set
        <div className="bg-gray-100 p-2 flex flex-col items-center">
          {/* HTML audio element */}
          <audio src={audioSrc} controls autoPlay className="w-full mb-2" />
          {/* Button to stop music */}
          <button className="px-3 py-1 bg-red-500 text-white rounded" onClick={() => setAudioSrc(null)}>Stop Music</button>
        </div>
      )}
      {/* Input Bar for typing messages */}
      <div className="bg-white p-3 border-t border-gray-300 flex items-center">
        {/* Input field */}
        <input
          className="flex-1 p-2 border border-gray-300 rounded-full focus:outline-none"
          value={input} // Bind value to input state
          onChange={e => setInput(e.target.value)} // Update state on change
          onKeyDown={e => e.key==='Enter' && (e.preventDefault(), sendMessage())} // Send message on Enter key press
          placeholder={isChangingName ? "Enter your new name" : "Message"} // Change placeholder when changing name
        />
        {/* Send button */}
        <button
          className="ml-2 bg-blue-500 p-2 rounded-full text-white disabled:opacity-50"
          onClick={sendMessage} // Call sendMessage on click
          disabled={isSending} // Disable button while sending
        >Send</button>
      </div>
      {/* Footer with information and sound toggle */}
      <div className="bg-white p-2 border-t border-gray-300 text-xs text-gray-500 flex justify-between">
        {/* Text source indicator */}
        <span>Text by {usingChatGPT ? 'ChatGPT' : 'BeCapy'}</span>
        {/* Sound toggle button and error indicator */}
        <button
          className="focus:outline-none"
          onClick={() => {
            setSoundOn(!soundOn); // Toggle sound state
            safeSet('soundOn', String(!soundOn)); // Persist sound state
          }}
        >
          Voice: {soundOn ? 'On' : 'Off'}
          {knowledgeLoadError && ' :('} {/* Display ":(" if knowledgeLoadError is true */}
        </button>
      </div>
    </div>
  );
}
